import { useContext, useEffect, useState } from 'react'

import { Box, Button, Text } from '@fairfashion/design-system'

import { OriginItem } from '../../pages/OriginItems/origin-items.types'
import { BackButton } from '../BackButton/back-button.comp'
import { ConfirmModalContext } from '../ConfirmModal'
import { EvidenceForm } from '../EvidenceForm/evidence-form.comp'
import { InvoiceForm } from '../InvoiceForm/invoice-form'
import { ItemCard } from '../ItemCard/item-card.comp'
import { ItemForm } from '../ItemForm/item-form'
import { ProviderForm } from '../ProviderForm/provider-form'

import { NewItemProps } from './new-item.types'

export const NewItem = ({
  itemCode,
  currentNode,
  editMode,
  onCancel,
  onDeleteNode,
  onSubmit,
  error,
}: NewItemProps) => {
  const [newItemData, setNewItemData] = useState<OriginItem>({} as OriginItem)
  const [openProviderModal, setOpenProviderModal] = useState(false)
  const [openItemModal, setOpenItemModal] = useState(false)
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false)
  const [openEvidenceModal, setOpenEvidenceModal] = useState(false)
  const isFormValid =
    !!newItemData.itemProviderRelation &&
    !!newItemData.code &&
    !!newItemData.description &&
    !!newItemData.taxUnit
  const isFormProviderValid = !!newItemData.itemProviderRelation

  const isFormOrignValid =
    !!newItemData.code && !!newItemData.description && !!newItemData.taxUnit

  const isInEditMode = editMode && editMode === currentNode?.code
  const initialNewData = () => {
    if (currentNode) {
      if (isInEditMode) {
        setNewItemData(currentNode)
      } else {
        const nodeId = crypto.randomUUID()

        setNewItemData({
          ...newItemData,
          nodeId: nodeId,
          referenceId: nodeId,
        })
      }
    }
  }

  const { confirmModal } = useContext(ConfirmModalContext)

  useEffect(() => {
    initialNewData()
  }, [])

  const handleDelete = async () => {
    if (!currentNode) return

    if (
      await confirmModal(
        'Excluir item',
        'Deseja realmente excluir este item? Ao excluir não será possível recuperar os dados dele.',
      )
    ) {
      onDeleteNode?.(currentNode?.id)
    }
  }

  return (
    <Box width="100%" height="100%" position="relative" direction="column">
      <Box width="100%" padding={3} direction="column" flex={1}>
        <Box gap={1} alignItems="center">
          <BackButton onClick={onCancel} />
          <Text color="grey_3" size="small">
            Atenção: certifique-se de salvar antes de sair, caso contrário seus
            dados serão perdidos.
          </Text>
        </Box>
        <Box
          width="100%"
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <Box
            gap={2}
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            paddingY={3}
          >
            <Text size="medium">
              {isInEditMode ? 'Editar Item' : 'Cadastrar novo item de origem'}
            </Text>
            <Box gap={2}>
              <Button variant="outlined" onClick={onCancel}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid}
                onClick={() => onSubmit?.(newItemData)}
              >
                Salvar
              </Button>
            </Box>
          </Box>
          {error && (
            <Box justifyContent="flex-end" width="100%" paddingBottom={3}>
              <Text color="error">{error}</Text>
            </Box>
          )}
          <Box width="100%" direction="column" gap={3}>
            <ItemCard
              icon="store2"
              title="Dados do Fornecedor"
              iconColor="primary"
              onClickAddButton={() => setOpenProviderModal(true)}
              error={!isFormProviderValid}
              render={
                <ProviderForm
                  onSubmit={(provider) => {
                    setNewItemData((prevNewItemData) => ({
                      ...prevNewItemData,
                      itemProviderRelation: {
                        companyProviderTaxId: provider.taxId,
                        companyProviderEmail: provider.email,
                        companyProviderName: provider.name,
                        companyProviderSegment: provider.segment,
                        companyClientTaxId:
                          currentNode?.itemProviderRelation
                            ?.companyProviderTaxId,
                      },
                    }))
                  }}
                  open={openProviderModal}
                  setOpen={setOpenProviderModal}
                  currentNode={currentNode || undefined}
                  editMode={editMode}
                />
              }
              hasData={!!newItemData.itemProviderRelation}
            />
            <ItemCard
              icon="shapes"
              title={isInEditMode ? 'Item' : 'Origem'}
              iconColor="purple"
              onClickAddButton={() => setOpenItemModal(true)}
              error={!isFormOrignValid}
              render={
                <ItemForm
                  itemCode={itemCode}
                  onSubmit={(item) => {
                    setNewItemData((prevNewItemData) => ({
                      ...prevNewItemData,
                      description: item.description,
                      quantity: item.quantity,
                      taxUnit: item.taxUnit,
                      code: item.code,
                      metadata: item.metadata,
                    }))
                  }}
                  data={newItemData}
                  open={openItemModal}
                  setOpen={setOpenItemModal}
                />
              }
              hasData={!!newItemData.code}
            />
            <ItemCard
              icon="store2"
              title="Nota Fiscal"
              iconColor="secondary"
              onClickAddButton={() => setOpenInvoiceModal(true)}
              render={
                <InvoiceForm
                  data={newItemData.itemInvoice}
                  onSubmit={(invoice) => {
                    setNewItemData((prevNewItemData) => ({
                      ...prevNewItemData,
                      itemInvoice: {
                        ...invoice,
                      },
                    }))
                  }}
                  open={openInvoiceModal}
                  setOpen={setOpenInvoiceModal}
                />
              }
              hasData={!!newItemData.itemInvoice}
            />
            <ItemCard
              icon="file"
              title="Arquivos de Evidência"
              iconColor="gray_blue"
              onClickAddButton={() => setOpenEvidenceModal(true)}
              render={
                <EvidenceForm
                  open={openEvidenceModal}
                  setOpen={setOpenEvidenceModal}
                  data={newItemData}
                  onRemoveEvidence={(index: number) => {
                    setNewItemData((prevNewItemData) => ({
                      ...prevNewItemData,
                      evidence: {
                        files: [
                          ...(prevNewItemData.evidence?.files ?? []).filter(
                            (_, i) => i !== index,
                          ),
                        ],
                      },
                    }))
                  }}
                  onSubmit={(data) => {
                    setNewItemData((prevNewItemData) => ({
                      ...prevNewItemData,
                      evidence: {
                        files: [
                          ...(prevNewItemData.evidence?.files ?? []),
                          data,
                        ],
                      },
                    }))
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>
      {isInEditMode && (
        <Box justifyContent="flex-end" width="100%" padding={3}>
          <Button variant="outlined" color="error" onClick={handleDelete}>
            Excluir
          </Button>
        </Box>
      )}
    </Box>
  )
}
